import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, History, RotateCcw, Loader2, Sparkles, PenLine, Clock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const SOURCE_ICON = { ai: Sparkles, manual: PenLine, unset: Clock };

export default function ChallengeDayVersionHistory({ challengeId, day, onClose, onRestored }) {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoringIdx, setRestoringIdx] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/custom-challenges/${challengeId}/day/${day.day}/versions`);
        setVersions(data.versions || []);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load version history");
      } finally {
        setLoading(false);
      }
    })();
  }, [challengeId, day.day]);

  const restore = async (idx) => {
    setRestoringIdx(idx);
    try {
      const { data } = await api.post(`/custom-challenges/${challengeId}/day/${day.day}/versions/restore`, { version_index: idx });
      toast.success("Version restored");
      onRestored(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to restore this version");
    } finally {
      setRestoringIdx(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center px-4" data-testid="version-history-modal">
      <div className="glass-panel rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-white text-sm flex items-center gap-2"><History className="w-4 h-4 text-indigo-300" /> Version History — Day {day.day}</h3>
          <button onClick={onClose} data-testid="version-history-close-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 text-indigo-300 animate-spin" /></div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-slate-400 py-4 text-center" data-testid="version-history-empty">No previous versions yet — edits and regenerations will appear here.</p>
        ) : (
          <div className="space-y-2.5">
            {versions.map((v, idx) => {
              const Icon = SOURCE_ICON[v.source] || Clock;
              return (
                <div key={idx} className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between gap-3" data-testid={`version-row-${idx}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-slate-400" /> {v.title}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">{new Date(v.saved_at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => restore(idx)} disabled={restoringIdx !== null} data-testid={`version-restore-btn-${idx}`}
                    className="shrink-0 flex items-center gap-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-slate-200 px-3 py-1.5 rounded-lg disabled:opacity-60">
                    {restoringIdx === idx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Restore
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
